// Read File Tool - Provides file reading functions for workspace
// Extends WorkspaceTool - has access to appType and toolSystem

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

const utf8Decoder =
  typeof TextDecoder !== "undefined" ? new TextDecoder("utf-8") : null;

export class ReadFileTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'readFileTool';
  }

  /**
   * Read file content
   * @param {string} path - Path to the file to read
   * @param {string} [encoding='utf8'] - File encoding (utf8 or binary)
   * @returns {Promise<Object>} Result with file content
   */
  async readFile(path, encoding = "utf8") {
    try {
      // Validate path
      if (!path || typeof path !== "string") {
        return this.returnError({}, "Valid file path required");
      }

      // Route based on app type
      if (this.appType === "browser") {
        return await this.readFileForBrowser(path, encoding);
      }
      if (this.appType === "local") {
        return await this.readFileForLocal(path, encoding);
      }
      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for readFile`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|readfile|error",
          color1: "red",
        },
        `Failed to read file: ${path}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Read file for browser apps using Lightning FS
   */
  async readFileForBrowser(path, encoding) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    // Normalize path - Lightning FS uses absolute paths
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    // Prepend /files/ to the filesystem path
    const filesystemPath = `/files${normalizedPath}`;

    try {
      // Check if file exists
      const stats = await fs.stat(filesystemPath);
      const isFile =
        typeof stats.isDirectory === "function"
          ? !stats.isDirectory()
          : stats.type !== "dir";

      if (!isFile) {
        return this.returnError({}, `Path is a directory, not a file: ${path}`);
      }

      // Read file content
      const buffer = await fs.readFile(filesystemPath);

      // Decode content based on encoding
      let content;
      if (encoding === "binary" || encoding === "base64") {
        // Return as base64 for binary files
        content = this.encodeBase64(buffer);
      } else {
        // Decode as UTF-8 text
        content = this.decodeUint8(buffer);
      }

      logger.log(
        {
          tags: "tools|readfile|info",
          color1: "lime",
        },
        `Read file: ${filesystemPath}`,
        {
          size: buffer.byteLength || buffer.length,
          encoding,
        }
      );

      return this.returnSuccess(
        {
          path: normalizedPath, // Return the original path without /files/ prefix
          content,
          encoding,
          size: buffer.byteLength || buffer.length,
        },
        "File read successfully"
      );
    } catch (error) {
      if (error.code === "ENOENT") {
        return this.returnError({}, `File not found: ${path}`);
      }
      throw error;
    }
  }

  /**
   * Read file for local apps (Revolt Desktop + workspaceLocalAppAPI).
   */
  async readFileForLocal(path, encoding) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.readFile) {
      return this.returnError(
        {},
        "Local file reads require Revolt Desktop (workspaceLocalAppAPI.readFile)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const normalizedPath = this.normalizeAndValidatePath(path);
    if (!normalizedPath) {
      return this.returnError({}, "Invalid file path");
    }
    const relativePath = normalizedPath.replace(/^\/+/, "");

    const enc =
      encoding === "binary" || encoding === "base64" ? encoding : "utf8";

    const res = await api.readFile({
      rootPath: root,
      relativePath,
      encoding: enc,
    });

    if (!res?.success) {
      return this.returnError({}, res?.error || "Failed to read file on disk");
    }

    logger.log(
      {
        tags: "tools|readfile|info",
        color1: "lime",
      },
      `Read file (local): ${normalizedPath}`,
      {
        size: res.size,
        encoding: enc,
      }
    );

    return this.returnSuccess(
      {
        path: normalizedPath,
        content: res.content,
        encoding: enc,
        size: res.size,
      },
      "File read successfully"
    );
  }

  /**
   * Check if a file exists
   * @param {string} path - Path to check
   * @returns {Promise<boolean>} True if file exists
   */
  async checkExists(path) {
    try {
      if (!path || typeof path !== "string") {
        return false;
      }

      if (this.appType === "browser") {
        return await this.checkExistsForBrowser(path);
      }
      if (this.appType === "local") {
        return await this.checkExistsForLocal(path);
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if file exists for browser apps
   */
  async checkExistsForBrowser(path) {
    const fs = this.fs?.promises;
    if (!fs) {
      return false;
    }

    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const filesystemPath = `/files${normalizedPath}`;

    try {
      await fs.stat(filesystemPath);
      return true;
    } catch (error) {
      if (error.code === "ENOENT") {
        return false;
      }
      throw error;
    }
  }

  /**
   * Check if path exists for local apps (file or directory).
   */
  async checkExistsForLocal(path) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.getPathInfo) {
      return false;
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return false;
    }

    const normalizedPath = this.normalizeAndValidatePath(path);
    if (!normalizedPath) {
      return false;
    }
    const relativePath = normalizedPath.replace(/^\/+/, "");

    try {
      const info = await api.getPathInfo({
        rootPath: root,
        relativePath,
      });
      return !!(info?.success && info.exists);
    } catch {
      return false;
    }
  }

  /**
   * Read file with offset and limit for partial reading
   * @param {string} path - Path to the file to read
   * @param {string} [encoding='utf8'] - File encoding
   * @param {number} [offset=0] - Starting byte offset
   * @param {number} [limit=null] - Maximum bytes to read (null = read to end)
   * @returns {Promise<Object>} Result with file content
   */
  async readFileWithRange(path, encoding = "utf8", offset = 0, limit = null) {
    try {
      // Validate path
      if (!path || typeof path !== "string") {
        return this.returnError({}, "Valid file path required");
      }

      // Validate offset and limit
      if (offset < 0) {
        return this.returnError({}, "Offset must be non-negative");
      }
      if (limit !== null && limit <= 0) {
        return this.returnError({}, "Limit must be positive");
      }

      // Route based on app type
      if (this.appType === "browser") {
        return await this.readFileWithRangeForBrowser(
          path,
          encoding,
          offset,
          limit
        );
      }
      if (this.appType === "local") {
        return await this.readFileWithRangeForLocal(
          path,
          encoding,
          offset,
          limit
        );
      }
      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for readFileWithRange`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|readfile|range|error",
          color1: "red",
        },
        `Failed to read file with range: ${path}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Read file with range for browser apps
   */
  async readFileWithRangeForBrowser(path, encoding, offset, limit) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const filesystemPath = `/files${normalizedPath}`;

    try {
      // Check if file exists
      const stats = await fs.stat(filesystemPath);
      const isFile =
        typeof stats.isDirectory === "function"
          ? !stats.isDirectory()
          : stats.type !== "dir";

      if (!isFile) {
        return this.returnError({}, `Path is a directory, not a file: ${path}`);
      }

      // Read file content
      const buffer = await fs.readFile(filesystemPath);

      // Validate offset
      if (offset >= buffer.length) {
        return this.returnError(
          {},
          `Offset ${offset} exceeds file size ${buffer.length}`
        );
      }

      // Apply offset and limit
      const endOffset =
        limit !== null
          ? Math.min(offset + limit, buffer.length)
          : buffer.length;
      const slicedBuffer = buffer.slice(offset, endOffset);

      // Decode content based on encoding
      let content;
      if (encoding === "binary" || encoding === "base64") {
        content = this.encodeBase64(slicedBuffer);
      } else {
        content = this.decodeUint8(slicedBuffer);
      }

      logger.log(
        {
          tags: "tools|readfile|range|info",
          color1: "lime",
        },
        `Read file with range: ${filesystemPath}`,
        {
          offset,
          limit,
          bytesRead: slicedBuffer.length,
          totalSize: buffer.length,
          encoding,
        }
      );

      return this.returnSuccess(
        {
          path: normalizedPath,
          content,
          encoding,
          offset,
          limit,
          bytesRead: slicedBuffer.length,
          totalSize: buffer.length,
        },
        "File read with range successfully"
      );
    } catch (error) {
      if (error.code === "ENOENT") {
        return this.returnError({}, `File not found: ${path}`);
      }
      throw error;
    }
  }

  /**
   * Read file with range for local apps (Revolt Desktop + workspaceLocalAppAPI).
   */
  async readFileWithRangeForLocal(path, encoding, offset, limit) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.readFileWithRange) {
      return this.returnError(
        {},
        "Local file reads require Revolt Desktop (workspaceLocalAppAPI.readFileWithRange)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const normalizedPath = this.normalizeAndValidatePath(path);
    if (!normalizedPath) {
      return this.returnError({}, "Invalid file path");
    }
    const relativePath = normalizedPath.replace(/^\/+/, "");

    const enc =
      encoding === "binary" || encoding === "base64" ? encoding : "utf8";

    const res = await api.readFileWithRange({
      rootPath: root,
      relativePath,
      encoding: enc,
      offset,
      limit,
    });

    if (!res?.success) {
      return this.returnError({}, res?.error || "Failed to read file on disk");
    }

    logger.log(
      {
        tags: "tools|readfile|range|info",
        color1: "lime",
      },
      `Read file with range (local): ${normalizedPath}`,
      {
        offset,
        limit,
        bytesRead: res.bytesRead,
        totalSize: res.totalSize,
        encoding: enc,
      }
    );

    return this.returnSuccess(
      {
        path: normalizedPath,
        content: res.content,
        encoding: res.encoding ?? enc,
        offset: res.offset,
        limit: res.limit,
        bytesRead: res.bytesRead,
        totalSize: res.totalSize,
      },
      "File read with range successfully"
    );
  }

  /**
   * Read file with line numbers and optional range
   * @param {string} path - Path to the file to read
   * @param {string} [encoding='utf8'] - File encoding
   * @param {number} [startLine=1] - Starting line number (1-indexed)
   * @param {number} [endLine=null] - Ending line number (1-indexed, null = read to end)
   * @returns {Promise<Object>} Result with file content in line-numbered format
   */
  async readFileWithLineNumbers(
    path,
    encoding = "utf8",
    startLine = 1,
    endLine = null
  ) {
    try {
      // Validate path
      if (!path || typeof path !== "string") {
        return this.returnError({}, "Valid file path required");
      }

      // Validate line numbers
      if (startLine < 1) {
        return this.returnError({}, "Start line must be at least 1");
      }
      if (endLine !== null && endLine < startLine) {
        return this.returnError(
          {},
          "End line must be greater than or equal to start line"
        );
      }

      // Route based on app type
      if (this.appType === "browser") {
        return await this.readFileWithLineNumbersForBrowser(
          path,
          encoding,
          startLine,
          endLine
        );
      }
      if (this.appType === "local") {
        return await this.readFileWithLineNumbersForLocal(
          path,
          encoding,
          startLine,
          endLine
        );
      }
      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for readFileWithLineNumbers`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|readfile|lines|error",
          color1: "red",
        },
        `Failed to read file with line numbers: ${path}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Read file with line numbers for browser apps
   */
  async readFileWithLineNumbersForBrowser(path, encoding, startLine, endLine) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const filesystemPath = `/files${normalizedPath}`;

    try {
      // Check if file exists
      const stats = await fs.stat(filesystemPath);
      const isFile =
        typeof stats.isDirectory === "function"
          ? !stats.isDirectory()
          : stats.type !== "dir";

      if (!isFile) {
        return this.returnError({}, `Path is a directory, not a file: ${path}`);
      }

      // Read file content
      const buffer = await fs.readFile(filesystemPath);

      // Decode content (only UTF-8 supported for line-based reading)
      if (encoding !== "utf8") {
        return this.returnError(
          {},
          "Line-numbered reading only supports UTF-8 encoding"
        );
      }

      const content = this.decodeUint8(buffer);

      // Handle empty files
      if (content.length === 0) {
        return this.returnSuccess(
          {
            path: normalizedPath,
            lines: [],
            totalLines: 0,
            startLine: 1,
            endLine: 0,
          },
          "File read successfully (empty file)"
        );
      }

      // Split into lines
      const allLines = content.split(/\r?\n/);
      const totalLines = allLines.length;

      // Validate start line
      if (startLine > totalLines) {
        return this.returnError(
          {},
          `Start line ${startLine} exceeds total lines ${totalLines}. ` +
            "Remember: `offset` is a 1-based line number, not a character index or token position — use `totalLines` from a previous read, read from the start with a `limit`, or use grep to locate content."
        );
      }

      // Calculate end line
      const actualEndLine =
        endLine !== null ? Math.min(endLine, totalLines) : totalLines;

      // Extract requested lines (convert to 0-indexed)
      const requestedLines = allLines.slice(startLine - 1, actualEndLine);

      // Format lines with line numbers
      const numberedLines = requestedLines.map((line, index) => ({
        lineNumber: startLine + index,
        content: line,
      }));

      logger.log(
        {
          tags: "tools|readfile|lines|info",
          color1: "lime",
        },
        `Read file with line numbers: ${filesystemPath}`,
        {
          startLine,
          endLine: actualEndLine,
          linesRead: numberedLines.length,
          totalLines,
        }
      );

      return this.returnSuccess(
        {
          path: normalizedPath,
          lines: numberedLines,
          totalLines,
          startLine,
          endLine: actualEndLine,
        },
        "File read with line numbers successfully"
      );
    } catch (error) {
      if (error.code === "ENOENT") {
        return this.returnError({}, `File not found: ${path}`);
      }
      throw error;
    }
  }

  /**
   * Read file with line numbers for local apps.
   */
  async readFileWithLineNumbersForLocal(path, encoding, startLine, endLine) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.readFile) {
      return this.returnError(
        {},
        "Local file reads require Revolt Desktop (workspaceLocalAppAPI.readFile)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const normalizedPath = this.normalizeAndValidatePath(path);
    if (!normalizedPath) {
      return this.returnError({}, "Invalid file path");
    }
    const relativePath = normalizedPath.replace(/^\/+/, "");

    if (encoding !== "utf8") {
      return this.returnError(
        {},
        "Line-numbered reading only supports UTF-8 encoding"
      );
    }

    const res = await api.readFile({
      rootPath: root,
      relativePath,
      encoding: "utf8",
    });

    if (!res?.success) {
      return this.returnError({}, res?.error || "Failed to read file on disk");
    }

    const content = res.content;

    if (content.length === 0) {
      return this.returnSuccess(
        {
          path: normalizedPath,
          lines: [],
          totalLines: 0,
          startLine: 1,
          endLine: 0,
        },
        "File read successfully (empty file)"
      );
    }

    const allLines = content.split(/\r?\n/);
    const totalLines = allLines.length;

    if (startLine > totalLines) {
      return this.returnError(
        {},
        `Start line ${startLine} exceeds total lines ${totalLines}. ` +
          "Remember: `offset` is a 1-based line number, not a character index or token position — use `totalLines` from a previous read, read from the start with a `limit`, or use grep to locate content."
      );
    }

    const actualEndLine =
      endLine !== null ? Math.min(endLine, totalLines) : totalLines;

    const requestedLines = allLines.slice(startLine - 1, actualEndLine);

    const numberedLines = requestedLines.map((line, index) => ({
      lineNumber: startLine + index,
      content: line,
    }));

    logger.log(
      {
        tags: "tools|readfile|lines|info",
        color1: "lime",
      },
      `Read file with line numbers (local): ${normalizedPath}`,
      {
        startLine,
        endLine: actualEndLine,
        linesRead: numberedLines.length,
        totalLines,
      }
    );

    return this.returnSuccess(
      {
        path: normalizedPath,
        lines: numberedLines,
        totalLines,
        startLine,
        endLine: actualEndLine,
      },
      "File read with line numbers successfully"
    );
  }

  /**
   * Decode Uint8Array to string
   */
  decodeUint8(buffer) {
    if (utf8Decoder) {
      try {
        return utf8Decoder.decode(buffer);
      } catch (error) {
        logger.warn(
          {
            tags: "tools|readfile|decode",
            color1: "yellow",
          },
          "Failed to decode file as UTF-8, falling back to manual decode",
          error
        );
      }
    }
    // Fallback for older browsers
    try {
      return Array.from(buffer || [])
        .map((byte) => String.fromCharCode(byte))
        .join("");
    } catch (error) {
      logger.warn(
        {
          tags: "tools|readfile|decode",
          color1: "yellow",
        },
        "Failed to decode file buffer",
        error
      );
      return "";
    }
  }

  /**
   * Encode buffer to base64
   */
  encodeBase64(buffer) {
    if (typeof btoa === "function") {
      const binary = Array.from(buffer)
        .map((byte) => String.fromCharCode(byte))
        .join("");
      return btoa(binary);
    }
    if (typeof Buffer !== "undefined") {
      return Buffer.from(buffer).toString("base64");
    }
    throw new Error("Unable to encode content as base64");
  }

}
